import { useState, useRef, useCallback, useEffect } from 'react';
import type { CallSignalMessage } from '@sahaj/types';

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export interface UseWebRTCOptions {
  onSignal: (signal: Partial<CallSignalMessage>) => void;
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
}

export function useWebRTC({ onSignal, onConnectionStateChange }: UseWebRTCOptions) {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const originalCameraTrackRef = useRef<MediaStreamTrack | null>(null);
  const screenSenderRef = useRef<RTCRtpSender | null>(null);

  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);
  const makingOfferRef = useRef(false);
  const isSettingRemoteAnswerPendingRef = useRef(false);
  // Whether this peer should initiate the first offer (i.e., is the CALLER)
  const isInitiatorRef = useRef(false);
  // Track the current callId for logging
  const callIdRef = useRef<string>('');

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);

  const onSignalRef = useRef(onSignal);
  onSignalRef.current = onSignal;

  const onConnectionStateChangeRef = useRef(onConnectionStateChange);
  onConnectionStateChangeRef.current = onConnectionStateChange;

  const cleanupPeerConnection = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.ontrack = null;
      pcRef.current.onicecandidate = null;
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.onnegotiationneeded = null;
      pcRef.current.oniceconnectionstatechange = null;
      pcRef.current.onsignalingstatechange = null;
      pcRef.current.close();
      pcRef.current = null;
    }
    screenSenderRef.current = null;
    pendingCandidatesRef.current = [];
    makingOfferRef.current = false;
    isSettingRemoteAnswerPendingRef.current = false;
    isInitiatorRef.current = false;
    setRemoteStream(null);
  }, []);

  // Stable cleanup function — ZERO state dependencies
  const cleanup = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
    if (originalCameraTrackRef.current) {
      originalCameraTrackRef.current.stop();
      originalCameraTrackRef.current = null;
    }
    cleanupPeerConnection();

    setLocalStream(null);
    setIsMuted(false);
    setIsCameraOff(false);
    setIsScreenSharing(false);
    setAudioLevel(0);
  }, [cleanupPeerConnection]);

  // Acquire local media preview early or on connection
  const acquireMedia = useCallback(async (callType: 'AUDIO' | 'VIDEO'): Promise<MediaStream> => {
    if (localStreamRef.current) {
      const audioLive = localStreamRef.current.getAudioTracks().some((t) => t.readyState === 'live');
      const videoLive = callType === 'AUDIO' || localStreamRef.current.getVideoTracks().some((t) => t.readyState === 'live');
      if (audioLive && videoLive) {
        return localStreamRef.current;
      }
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: callType === 'VIDEO'
          ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } }
          : false,
      });

      localStreamRef.current = stream;
      setLocalStream(stream);

      if (callType === 'VIDEO') {
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) originalCameraTrackRef.current = videoTrack;
      }

      // Audio visualizer setup
      try {
        if (!audioContextRef.current) {
          const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
          audioContextRef.current = audioCtx;
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 64;
          source.connect(analyser);
          analyserRef.current = analyser;

          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          const checkVolume = () => {
            if (!analyserRef.current) return;
            analyserRef.current.getByteFrequencyData(dataArray);
            const avg = dataArray.reduce((acc, v) => acc + v, 0) / dataArray.length;
            setAudioLevel(Math.min(100, Math.round((avg / 128) * 100)));
            animFrameRef.current = requestAnimationFrame(checkVolume);
          };
          checkVolume();
        }
      } catch (_e) {
        // ignore
      }

      return stream;
    } catch (err) {
      console.error('[WebRTC] acquireMedia FAILED:', err);
      throw err;
    }
  }, []);

  // Flush queued ICE candidates once remote description is set
  const flushPendingCandidates = useCallback(async () => {
    if (!pcRef.current || !pcRef.current.remoteDescription) return;
    const candidates = [...pendingCandidatesRef.current];
    pendingCandidatesRef.current = [];
    for (const candidate of candidates) {
      try {
        await pcRef.current.addIceCandidate(candidate);
        console.log(`[CALL:${callIdRef.current}] Flushed buffered ICE candidate`);
      } catch (err) {
        console.warn(`[CALL:${callIdRef.current}] Failed to flush ICE candidate`, err);
      }
    }
  }, []);

  /**
   * Initialize RTCPeerConnection and acquire local media.
   */
  const startConnection = useCallback(
    async (
      callType: 'AUDIO' | 'VIDEO',
      _callerId: string,
      _recipientId: string,
      callId: string,
      isInitiator: boolean = false
    ) => {
      cleanupPeerConnection();

      callIdRef.current = callId;
      isInitiatorRef.current = isInitiator;

      const role = isInitiator ? 'CALLER' : 'CALLEE';
      console.log(`[CALL:${callId}][${role}] startConnection() — acquiring media`);

      // Acquire or reuse local media
      let stream: MediaStream;
      try {
        stream = await acquireMedia(callType);
        console.log(`[CALL:${callId}][${role}] getUserMedia OK — tracks:`, stream.getTracks().map(t => `${t.kind}(${t.readyState})`).join(', '));
      } catch (err) {
        console.error(`[CALL:${callId}][${role}] getUserMedia FAILED:`, err);
        throw err;
      }

      const pc = new RTCPeerConnection({ iceServers: DEFAULT_ICE_SERVERS });
      pcRef.current = pc;

      console.log(`[CALL:${callId}][${role}] RTCPeerConnection created`);

      // Add local tracks
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
        console.log(`[CALL:${callId}][${role}] addTrack: ${track.kind}`);
      });

      // Remote track handler
      pc.ontrack = (event) => {
        console.log(`[CALL:${callId}][${role}] REMOTE_TRACK_RECEIVED: kind=${event.track.kind} readyState=${event.track.readyState} enabled=${event.track.enabled}`);
        let rStream: MediaStream;
        if (event.streams && event.streams[0]) {
          rStream = event.streams[0];
        } else {
          rStream = new MediaStream([event.track]);
        }

        event.track.onunmute = () => {
          console.log(`[CALL:${callId}][${role}] Remote track unmuted: kind=${event.track.kind}`);
          setRemoteStream(rStream);
        };

        setRemoteStream(rStream);
      };


      // ICE candidate handler
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          console.log(`[CALL:${callId}][${role}] ICE candidate generated: ${event.candidate.type}`);
          onSignalRef.current({
            callId,
            type: 'ICE_CANDIDATE',
            payload: { candidate: event.candidate.toJSON() },
          });
        } else {
          console.log(`[CALL:${callId}][${role}] ICE gathering complete`);
        }
      };

      // Connection state tracking
      pc.onconnectionstatechange = () => {
        console.log(`[CALL:${callId}][${role}] connectionState: ${pc.connectionState} | iceConnectionState: ${pc.iceConnectionState} | signalingState: ${pc.signalingState}`);
        onConnectionStateChangeRef.current?.(pc.connectionState);
      };

      pc.oniceconnectionstatechange = () => {
        console.log(`[CALL:${callId}][${role}] iceConnectionState: ${pc.iceConnectionState}`);
      };

      pc.onsignalingstatechange = () => {
        console.log(`[CALL:${callId}][${role}] signalingState: ${pc.signalingState}`);
      };

      // Perfect Negotiation — onnegotiationneeded
      // KEY: Only the INITIATOR (caller) sends the first offer.
      // The callee's onnegotiationneeded will fire when tracks are added but we skip
      // the offer because isInitiatorRef.current === false at that point.
      // After the caller's offer is answered, renegotiation (e.g., screen share) is
      // handled by Perfect Negotiation with polite/impolite roles.
      pc.onnegotiationneeded = async () => {
        console.log(`[CALL:${callId}][${role}] onnegotiationneeded — isInitiator:${isInitiatorRef.current} signalingState:${pc.signalingState}`);

        // Only create offer if:
        // 1. We are the initiator (caller), OR
        // 2. We are in an established call and renegotiation is needed (e.g., screen share)
        //    indicated by signalingState === 'stable' AND remoteDescription already set
        const isRenegotiation = pc.remoteDescription !== null && pc.signalingState === 'stable';
        if (!isInitiatorRef.current && !isRenegotiation) {
          console.log(`[CALL:${callId}][${role}] Skipping offer — callee waits for caller's offer`);
          return;
        }

        try {
          makingOfferRef.current = true;
          await pc.setLocalDescription();
          if (pc.localDescription) {
            console.log(`[CALL:${callId}][${role}] Sending CALL_OFFER`);
            onSignalRef.current({
              callId,
              type: 'CALL_OFFER',
              payload: { sdp: pc.localDescription.sdp },
            });
          }
        } catch (err) {
          console.error(`[CALL:${callId}][${role}] Offer generation error:`, err);
        } finally {
          makingOfferRef.current = false;
        }
      };

      return pc;
    },
    [cleanup]
  );

  // Handle incoming remote SDP/ICE signals
  const handleRemoteSignal = useCallback(
    async (signal: CallSignalMessage, _callerId: string, _recipientId: string) => {
      const pc = pcRef.current;
      if (!pc) {
        console.warn(`[CALL:${callIdRef.current}] handleRemoteSignal called but no RTCPeerConnection`);
        return;
      }

      const role = isInitiatorRef.current ? 'CALLER' : 'CALLEE';
      // Polite peer = the one who did NOT initiate = callee
      const isPolite = !isInitiatorRef.current;

      console.log(`[CALL:${callIdRef.current}][${role}] handleRemoteSignal: ${signal.type} | signalingState:${pc.signalingState} | makingOffer:${makingOfferRef.current} | isPolite:${isPolite}`);

      if (signal.type === 'CALL_OFFER' && signal.payload?.sdp) {
        const offerCollision =
          makingOfferRef.current ||
          pc.signalingState !== 'stable' ||
          isSettingRemoteAnswerPendingRef.current;

        // Impolite peer (caller) ignores offer collisions
        const ignoreOffer = !isPolite && offerCollision;
        if (ignoreOffer) {
          console.log(`[CALL:${callIdRef.current}][${role}] Impolite — ignoring offer collision`);
          return;
        }

        if (offerCollision && isPolite) {
          console.log(`[CALL:${callIdRef.current}][${role}] Polite — rolling back local offer`);
          await pc.setLocalDescription({ type: 'rollback' });
          makingOfferRef.current = false;
        }

        isSettingRemoteAnswerPendingRef.current = true;
        await pc.setRemoteDescription({ type: 'offer', sdp: signal.payload.sdp });
        isSettingRemoteAnswerPendingRef.current = false;

        // After setting remote offer, callee is now ready to send answer
        // Mark as renegotiation so future onnegotiationneeded creates offers
        isInitiatorRef.current = true; // Both sides can now make offers for renegotiation

        await pc.setLocalDescription();
        if (pc.localDescription) {
          console.log(`[CALL:${callIdRef.current}][${role}] Sending CALL_ANSWER`);
          onSignalRef.current({
            callId: signal.callId,
            type: 'CALL_ANSWER',
            payload: { sdp: pc.localDescription.sdp },
          });
        }
        await flushPendingCandidates();
      } else if (signal.type === 'CALL_ANSWER' && signal.payload?.sdp) {
        console.log(`[CALL:${callIdRef.current}][${role}] Setting remote ANSWER`);
        await pc.setRemoteDescription({ type: 'answer', sdp: signal.payload.sdp });
        await flushPendingCandidates();
      } else if (signal.type === 'ICE_CANDIDATE' && signal.payload?.candidate) {
        const candidate = signal.payload.candidate;
        if (pc.remoteDescription) {
          try {
            await pc.addIceCandidate(candidate);
          } catch (err) {
            console.warn(`[CALL:${callIdRef.current}][${role}] Failed to add ICE candidate:`, err);
          }
        } else {
          console.log(`[CALL:${callIdRef.current}][${role}] Buffering ICE candidate (no remote description yet)`);
          pendingCandidatesRef.current.push(candidate);
        }
      }
    },
    [flushPendingCandidates]
  );

  // Media Controls
  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;
    const audioTrack = localStreamRef.current.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      setIsMuted(!audioTrack.enabled);
      console.log(`[CALL:${callIdRef.current}] Mute: ${!audioTrack.enabled}`);
    }
  }, []);

  const toggleCamera = useCallback(() => {
    if (!localStreamRef.current) return;
    const videoTrack = localStreamRef.current.getVideoTracks()[0];
    if (videoTrack) {
      videoTrack.enabled = !videoTrack.enabled;
      setIsCameraOff(!videoTrack.enabled);
      console.log(`[CALL:${callIdRef.current}] Camera off: ${!videoTrack.enabled}`);
    }
  }, []);

  const toggleScreenShare = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc || !localStreamRef.current) return;

    if (isScreenSharing) {
      if (screenSenderRef.current) {
        if (originalCameraTrackRef.current) {
          await screenSenderRef.current.replaceTrack(originalCameraTrackRef.current);
        } else {
          pc.removeTrack(screenSenderRef.current);
          screenSenderRef.current = null;
        }
      } else {
        const videoSender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (videoSender && originalCameraTrackRef.current) {
          await videoSender.replaceTrack(originalCameraTrackRef.current);
        }
      }
      setIsScreenSharing(false);
      setScreenStream(null);
    } else {
      try {
        const capturedScreenStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
        const screenTrack = capturedScreenStream.getVideoTracks()[0];
        if (!screenTrack) return;

        let videoSender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (videoSender) {
          await videoSender.replaceTrack(screenTrack);
          screenSenderRef.current = videoSender;
        } else {
          // Audio-only call: add video track (triggers onnegotiationneeded renegotiation)
          const sender = pc.addTrack(screenTrack, localStreamRef.current);
          screenSenderRef.current = sender;
        }

        // Expose screen stream so sharer can see their own screen in PiP
        setScreenStream(capturedScreenStream);
        setIsScreenSharing(true);

        screenTrack.onended = async () => {
          if (screenSenderRef.current) {
            if (originalCameraTrackRef.current) {
              await screenSenderRef.current.replaceTrack(originalCameraTrackRef.current);
            } else {
              pc.removeTrack(screenSenderRef.current);
              screenSenderRef.current = null;
            }
          }
          setIsScreenSharing(false);
          setScreenStream(null);
        };
      } catch (err) {
        console.warn(`[CALL:${callIdRef.current}] Screen share cancelled or denied:`, err);
      }
    }
  }, [isScreenSharing]);

  // Cleanup on hook unmount ONLY
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    localStream,
    remoteStream,
    screenStream,
    isMuted,
    isCameraOff,
    isScreenSharing,
    audioLevel,
    acquireMedia,
    startConnection,
    handleRemoteSignal,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    cleanup,
  };
}
